import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { X, Rocket, Eye, EyeOff, Loader2, Trash2, Database, CheckCircle2 } from "lucide-react";
import { apiPost } from "@/lib/api";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { clearDemoProfile } from "@/lib/demo-profile";

interface Props {
  open: boolean;
  email: string;
  companyName: string;
  currentPlanId?: string;
  onClose: () => void;
}

type Choice = "fresh" | "keep";

export function DemoUpgradeModal({ open, email, companyName, currentPlanId, onClose }: Props) {
  const [password, setPassword]     = useState("");
  const [showPwd, setShowPwd]       = useState(false);
  const [choice, setChoice]         = useState<Choice>("fresh");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [, setLocation]             = useLocation();
  const qc                          = useQueryClient();

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError("");
    setLoading(true);
    try {
      const result = await apiPost<{ success: boolean; newSlug?: string }>("/auth/demo/upgrade", {
        password, keepDemoData: choice === "keep", planId: currentPlanId,
      });
      clearDemoProfile();
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      setLocation(result.newSlug ? `/app/${result.newSlug}` : "/");
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background: "rgba(5,15,30,0.82)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 520 }}>
        <div className="h-1.5 w-full shrink-0" style={{ background: "linear-gradient(90deg, #0d4a4a 0%, #1a3a5c 100%)" }} />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-lg hover:bg-slate-100 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 pt-7 pb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md shrink-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Activate your account</h2>
              <p className="text-sm text-slate-500">{companyName}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Email</label>
              <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-500 select-all">
                {email}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                Set a password
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full px-3 py-2.5 pr-10 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">What happens to your demo data?</p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setChoice("fresh")}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                    choice === "fresh"
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${choice === "fresh" ? "bg-blue-100" : "bg-slate-100"}`}>
                    <Trash2 className={`w-4 h-4 ${choice === "fresh" ? "text-blue-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${choice === "fresh" ? "text-blue-700" : "text-slate-700"}`}>
                      Start with a clean workspace
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                      All sample demo data is wiped. You start fresh with only your company settings.
                    </p>
                  </div>
                  {choice === "fresh" && <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-1 ml-auto" />}
                </button>

                <button
                  type="button"
                  onClick={() => setChoice("keep")}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                    choice === "keep"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${choice === "keep" ? "bg-emerald-100" : "bg-slate-100"}`}>
                    <Database className={`w-4 h-4 ${choice === "keep" ? "text-emerald-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${choice === "keep" ? "text-emerald-700" : "text-slate-700"}`}>
                      Keep demo data — hit the ground running
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                      All sample projects, invoices, and records stay in place. You can delete them anytime.
                    </p>
                  </div>
                  {choice === "keep" && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-1 ml-auto" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity shadow-md text-sm disabled:opacity-70"
              style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating…</>
                : <><Rocket className="w-4 h-4" /> Activate Account</>
              }
            </button>

            <p className="text-center text-xs text-slate-400">
              No credit card required · You can set up billing later
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
